/**
 * Modèle de droits AGORA (Amazon Verified Permissions, schéma Cedar `Agora`).
 *
 * Ressources : Metier › Module › SousModule, Dimension, Champ (champ sensible).
 * Actions    : voirAccueil (accueil d'un métier, ouvert à tous), lire, creer, modifier, supprimer,
 *              valider (traitement courant : résoudre, clôturer…), approuver (décision d'un responsable désigné sur
 *              une demande, jamais donnée aux profils de traitement : validation d'une escalade par le DSI…,
 *              décision du DSI du 02/11/2026, D-IT-148), exporter, administrer.
 * Contexte   : filiale concernée (code FR01…).
 *
 * Développement (21/10/2026) : 2 profils globaux
 *   AGORA-GRP-ALL-LECTURE  → lire + exporter partout
 *   AGORA-GRP-ALL-ECRITURE → toutes les actions sauf approuver et administrer (administrer accordé sur le sous-module
 *                            it.demandes.incidents : référentiels IT)
 *   AGORA-GRP-IT-ADMIN     → tout
 * Production : profils découpés par filiale × métier × module × sous-module × dimension × champ
 * (convention de groupes Entra `AGORA-<périmètre>-<métier>-<profil>`, exemples dans infra/authz/exemples-prod).
 */
import { FILIALES } from './filiales';

export const ACTIONS = ['voirAccueil', 'lire', 'creer', 'modifier', 'supprimer', 'valider', 'approuver', 'exporter', 'administrer'] as const;
export type Action = (typeof ACTIONS)[number];

/** Actions renvoyées au front pour chaque nœud du catalogue. */
export const ACTIONS_NOEUD: Action[] = ['lire', 'creer', 'modifier', 'supprimer', 'valider', 'approuver', 'exporter', 'administrer'];

export const GROUPES = {
  utilisateur: 'AGORA-ALL-ALL-UTILISATEUR',
  admin: 'AGORA-GRP-IT-ADMIN',
  lecture: 'AGORA-GRP-ALL-LECTURE',
  ecriture: 'AGORA-GRP-ALL-ECRITURE',
} as const;

/** Un groupe AGORA : AGORA-<périmètre>-<métier>-<profil>. */
export function estGroupeAgora(nom: string): boolean {
  return /^AGORA-[A-Z0-9]+-[A-Z0-9]+-[A-Z0-9_]+$/.test(nom);
}

/**
 * Filiales autorisées déduites des groupes : périmètre ALL / GRP = toutes les filiales ;
 * périmètre = code filiale (FR01…) = cette filiale.
 */
export function filialesDesGroupes(groupes: string[]): string[] {
  const codes = new Set<string>();
  for (const g of groupes.filter(estGroupeAgora)) {
    const perimetre = g.split('-')[1];
    if (perimetre === 'ALL' || perimetre === 'GRP') return FILIALES.map((f) => f.code);
    if (FILIALES.some((f) => f.code === perimetre)) codes.add(perimetre);
  }
  return [...codes];
}

export type DroitsNoeud = Partial<Record<Action, boolean>>;
